import { permissionsEnumSchema } from "@mindlercare/schema";
import { Icon } from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import {
  useGetPsychologistIcbtCommunication,
  useGetPsychologistIcbtTasks,
} from "api/useIcbtQueries";
import { useCategorizedTodoCommunication } from "components/icbt/hooks/useCategorizedTodoCommunication";
import { hasPermissions } from "helpers/permissions";
import { usePsychologistUser } from "hooks";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentUser, useUserHasRole } from "../../hooks/userHooks";
import useMarketStrategy from "../../strategies/market/useMarketStrategy";
import { NumberOfNotifications } from "./drawer.styles";
import { DrawerToggleButton } from "./DrawerButtonItem";
import { DrawerContextProvider } from "./DrawerContextProvider";
import { DrawerFooter } from "./DrawerFooter";
import { DrawerHeader } from "./DrawerHeader";
import { DrawerExternalLinkItem, DrawerLinkItem } from "./DrawerLinkItem";
import { DrawerSection } from "./DrawerSection";
import { DrawerItemLabel } from "./DrawerItemLabel";
import { useSortedIcbtTodoTasks } from "components/icbt/hooks/useSortedIcbtTodoTasks";

export const Drawer = () => {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const isPsychologist = useUserHasRole("Psychologist");
  const psychologistId = usePsychologistUser().psychologist?.psychologistId;
  const isAssessmentEnabled = useFlag("ICBT_ASSESSMENT_FIELD");

  const { data: icbtCommunication } = useGetPsychologistIcbtCommunication(
    undefined,
    {
      enabled: !!psychologistId && !isAssessmentEnabled,
    },
  );

  const { data: icbtTasks } = useGetPsychologistIcbtTasks(undefined, {
    enabled: !!psychologistId && isAssessmentEnabled,
  });

  const isPsychologistDashboardEnabled = useFlag("PSYCHOLOGIST_DASHBOARD");
  const isChatbotClinicalGuidelinesEnabled = useFlag(
    "CHATBOT_CLINICAL_GUIDELINES",
  );
  const { marketHasStatisticsDashboard, clinicalGuidelinesHref } =
    useMarketStrategy();

  const hasExtractIcbtFeePermission = hasPermissions(
    user,
    permissionsEnumSchema.enum.EXTRACT_ICBT_FEE,
  );

  const hasUserPaymentsPermission = hasPermissions(
    user,
    permissionsEnumSchema.enum.USER_PAYMENTS,
  );

  const icbtCommunicationEnabled = useFlag("ICBT_COMMUNICATION_MC_FEATURES");

  const patientUserIdsIcbtComms = useMemo(
    () => [
      ...new Set(
        icbtCommunication?.icbtCommunications.map((icbt) =>
          Number(icbt.patient_user_id),
        ),
      ),
    ],
    [icbtCommunication],
  ).map((id) => ({
    userId: id,
  }));

  const patientUserIdsIcbtTasks = useMemo(
    () => [...new Set(icbtTasks?.map((task) => Number(task.icbtRequestId)))],
    [icbtTasks],
  ).map((id) => ({
    userId: id,
  }));

  const ignoreFeedbackDateFeatureEnabled = useFlag("NO_ICBT_FEEDBACK_NEEDED");

  const categorizedIcbtCommunication = useCategorizedTodoCommunication({
    icbtCommunication: icbtCommunication?.icbtCommunications,
    patientsToIncludeInCommunication: patientUserIdsIcbtComms,
    includeOnlyWithResponses: false,
    ignoreFeedbackDateFeatureEnabled,
  });

  const sortedIcbtTasks = useSortedIcbtTodoTasks({
    icbtTasks: icbtTasks,
    patientsToIncludeInCommunication: patientUserIdsIcbtTasks,
  });

  const numberOfNotificationsIcbtComms =
    categorizedIcbtCommunication.get("today")?.length ?? 0;

  const numberOfNotificationsIcbtTasks =
    sortedIcbtTasks.get("now")?.length ?? 0;

  const numberToUse = isAssessmentEnabled
    ? numberOfNotificationsIcbtTasks
    : numberOfNotificationsIcbtComms;

  return (
    <DrawerContextProvider>
      <DrawerHeader />
      <DrawerSection>
        <DrawerLinkItem path="/">
          <Icon.Calendar width={20} />
          <DrawerItemLabel>{t("CALENDAR")}</DrawerItemLabel>
        </DrawerLinkItem>
        <DrawerLinkItem path="/patients">
          <Icon.Heart width={20} />
          <DrawerItemLabel>{t("PATIENTS")}</DrawerItemLabel>
        </DrawerLinkItem>
        <DrawerLinkItem path="/icbt">
          <Icon.Icbt width={20} />
          <DrawerItemLabel>{t("ICBT")}</DrawerItemLabel>
        </DrawerLinkItem>
        {icbtCommunicationEnabled && !!psychologistId && (
          <DrawerLinkItem path="/icbt-feedback">
            {numberToUse > 0 ? (
              <Icon.FeedbackUnread width={20} />
            ) : (
              <Icon.Feedback width={20} />
            )}
            <DrawerItemLabel>
              {t("ICBT_FEEDBACK_TITLE")}
              {numberToUse > 0 && (
                <NumberOfNotifications>{numberToUse}</NumberOfNotifications>
              )}
            </DrawerItemLabel>
          </DrawerLinkItem>
        )}
        <DrawerLinkItem path="/cancelled-bookings">
          <Icon.Block width={20} />
          <DrawerItemLabel>{t("CANCELED_MEETINGS")}</DrawerItemLabel>
        </DrawerLinkItem>
        {hasUserPaymentsPermission && (
          <DrawerLinkItem path="/payments">
            <Icon.Payment width={20} />
            <DrawerItemLabel>{t("PAYMENTS")}</DrawerItemLabel>
          </DrawerLinkItem>
        )}
        {hasExtractIcbtFeePermission && (
          <DrawerLinkItem path="/icbt-payments">
            <Icon.Payment width={20} />
            <DrawerItemLabel>{t("ICBT_PAYMENTS")}</DrawerItemLabel>
          </DrawerLinkItem>
        )}
        {isPsychologistDashboardEnabled && (
          <DrawerLinkItem path="/psychologist-dashboard">
            <Icon.Analytics width={20} />
            <DrawerItemLabel>{t("ICBT_DASHBOARD")}</DrawerItemLabel>
          </DrawerLinkItem>
        )}
        {marketHasStatisticsDashboard && (
          <DrawerLinkItem path="/dashboard">
            <Icon.Analytics width={20} />
            <DrawerItemLabel>{t("DASHBOARD")}</DrawerItemLabel>
          </DrawerLinkItem>
        )}
        {isPsychologist && (
          <DrawerLinkItem path="/profile">
            <Icon.Profile width={20} />
            <DrawerItemLabel>{t("MY_PROFILE")}</DrawerItemLabel>
          </DrawerLinkItem>
        )}
        {isChatbotClinicalGuidelinesEnabled && (
          <DrawerLinkItem path="/chatbot/clinical-guidelines">
            <Icon.AutomaticSent width={20} />
            <DrawerItemLabel>
              {t("CHATBOT.CLINICAL_GUIDELINES.DRAWER_ITEM")}
            </DrawerItemLabel>
          </DrawerLinkItem>
        )}
      </DrawerSection>
      <DrawerSection title={t("SHORTCUTS")}>
        <DrawerExternalLinkItem path={clinicalGuidelinesHref}>
          <Icon.Shortcut width={20} />
          <DrawerItemLabel>{t("CLINICAL_GUIDELINES")}</DrawerItemLabel>
        </DrawerExternalLinkItem>
      </DrawerSection>
      <DrawerSection>
        <DrawerToggleButton />
      </DrawerSection>
      <DrawerFooter />
    </DrawerContextProvider>
  );
};
